import { useInfiniteQuery } from '@tanstack/react-query';
import { FetchAppealsParams, fetchAppeals } from 'api/appeals';
import { FilterGroupRequest, FilterRequestMap } from 'components/filter/types';
import { AppealStatus } from 'constants/appeals';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInfiniteScroll } from 'customHooks/useInfiniteScroll';
import { transformInfiniteQueryOptions } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Appeal } from 'types/domain';
import { GivebutterUseInfiniteQueryOptions } from 'types/react-query';

const DEFAULT_PAGE_LIMIT = 5;

const useInfiniteAppeals = (
    params: FetchAppealsParams = {},
    options: GivebutterUseInfiniteQueryOptions<Appeal> = {},
) => {
    const { limit = DEFAULT_PAGE_LIMIT, ...rest } = params;
    const filters: FilterGroupRequest<FilterRequestMap>[] = [
        {
            children: [
                {
                    type: 'and',
                    children: {
                        status: [
                            {
                                value: [AppealStatus.Active],
                                operator: '=',
                                type: 'and',
                            },
                        ],
                    },
                },
            ],
            type: 'and',
        },
    ];

    const nextParams = {
        limit,
        filters,
        ...rest,
    };
    const { accountId } = useCurrentAccount();
    const infiniteQueryData = useInfiniteQuery({
        initialPageParam: 1,
        queryKey: accountQueryKeys.appealsWithFilters(accountId, nextParams),
        queryFn: ({ pageParam }) => fetchAppeals(accountId, { page: pageParam, ...nextParams }),
        ...transformInfiniteQueryOptions<Appeal>(),
        ...options,
    });
    const ref = useInfiniteScroll(infiniteQueryData);

    return { ...infiniteQueryData, ref };
};

export { useInfiniteAppeals };
